import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { FolderKey } from "./folderTree";
import { LISTING_TOUR_ANCHOR } from "./listingTour";

interface NewProjectMenuProps {
  /** The folder a new project or folder is created in. */
  folderId: FolderKey;
  canCreateProject: boolean;
  canCreateFolder: boolean;
  onNewFolder: () => void;
  /** Start a project here instead of opening the full-page wizard. */
  onNewProject?: () => void;
  /** Browsing story maps: offer a story map in the open folder (or across every project). */
  canCreateStoryMap?: boolean;
  onNewStoryMap?: () => void;
  /** Off while a project is being set up beside the map. */
  disabled?: boolean;
}

/** The toolbar's "New" button: a project, a story map (browse mode) or a folder. */
export default function NewProjectMenu({
  folderId,
  canCreateProject,
  canCreateFolder,
  onNewFolder,
  onNewProject,
  canCreateStoryMap = false,
  onNewStoryMap,
  disabled = false,
}: NewProjectMenuProps) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const storyItem = canCreateStoryMap && !!onNewStoryMap;
  if (!canCreateProject && !canCreateFolder && !storyItem) return null;
  const close = () => setAnchor(null);
  const go = (path: string) => {
    close();
    navigate(path, { state: { folderId } });
  };
  return (
    <>
      <Button
        data-tour={LISTING_TOUR_ANCHOR.newMenu}
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<AddIcon />}
        endIcon={<ArrowDropDownIcon />}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
      >
        New
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        {canCreateProject && (
          <MenuItem
            onClick={() => {
              if (onNewProject) {
                close();
                onNewProject();
              } else go("/create-project");
            }}
          >
            <ListItemIcon>
              <MapOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Project" secondary="Pick a location and set it up" />
          </MenuItem>
        )}
        {storyItem && (
          <MenuItem
            onClick={() => {
              close();
              onNewStoryMap?.();
            }}
          >
            <ListItemIcon>
              <AutoStoriesOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Story map"
              secondary={folderId === null ? "Drawing on every project" : "Drawing on the projects in this folder"}
            />
          </MenuItem>
        )}
        {(canCreateProject || storyItem) && canCreateFolder && <Divider />}
        {canCreateFolder && (
          <MenuItem
            onClick={() => {
              close();
              onNewFolder();
            }}
          >
            <ListItemIcon>
              <CreateNewFolderOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Folder" secondary="Inside the folder you're looking at" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
